import { useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "motion/react";
import { createEvent, dayKey, dayStartOf, type RepeatRule } from "@otrack/domain";
import { Button, DateField, Field, FieldError, Icon, Modal, Segmented, Select, Switch, TextArea, TextInput, TimeField } from "@otrack/ui";
import { useApp, useDataset, useFactory } from "../context";
import { formatClock, fromTimeInput, startOfDay, toTimeInput } from "../time";
import type { Destination, PlanPatch, useCalendarCommands } from "./actions";
import { writableCalendars } from "./google";
import type { PlanItem } from "./model";
import { defaultDestination, destinationOf, destinationOptions, projectOptions, taskOptions } from "./popover";

type Preset = "none" | "daily" | "weekdays" | "weekly" | "monthly" | "custom";
const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0];

function presetOf(rule: RepeatRule | null, start: number): Preset {
  if (!rule) return "none";
  if (rule.until !== null || rule.count !== null) return "custom";
  const weekday = new Date(start).getDay();
  if (rule.freq === "daily" && rule.interval === 1) return "daily";
  if (rule.freq === "weekly" && rule.interval === 1 && rule.weekdays.length === 5 && [1, 2, 3, 4, 5].every((day) => rule.weekdays.includes(day))) return "weekdays";
  if (rule.freq === "weekly" && rule.interval === 1 && (rule.weekdays.length === 0 || (rule.weekdays.length === 1 && rule.weekdays[0] === weekday))) return "weekly";
  if (rule.freq === "monthly" && rule.interval === 1) return "monthly";
  return "custom";
}

function ruleOf(preset: Preset, start: number, custom: RepeatRule): RepeatRule | null {
  const weekday = new Date(start).getDay();
  switch (preset) {
    case "none": return null;
    case "daily": return { freq: "daily", interval: 1, weekdays: [], until: null, count: null };
    case "weekdays": return { freq: "weekly", interval: 1, weekdays: [1, 2, 3, 4, 5], until: null, count: null };
    case "weekly": return { freq: "weekly", interval: 1, weekdays: [weekday], until: null, count: null };
    case "monthly": return { freq: "monthly", interval: 1, weekdays: [], until: null, count: null };
    case "custom": return { ...custom, weekdays: custom.freq === "weekly" ? (custom.weekdays.length ? custom.weekdays : [weekday]) : [] };
  }
}

const sameRule = (a: RepeatRule | null, b: RepeatRule | null) => JSON.stringify(a) === JSON.stringify(b);

export interface EventDraft { start: number; end: number; allDay: boolean; title: string; projectId: string | null; taskId: string | null; destination?: Destination }

/**
 * The full Event editor: everything the popover has, plus repeats, day ranges, and notes. Google
 * events add their location and calendar; a repeat a Repeat can't express stays as Google has it.
 */
export function EventEditor({ plan, draft, commands, onClose }: { plan: PlanItem | null; draft: EventDraft | null; commands: ReturnType<typeof useCalendarCommands>; onClose: () => void }) {
  const { act, google } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const source = plan?.occurrence?.event;
  const seed = plan ?? draft!;
  const googlePlan = plan?.google ?? null;
  const seriesRule = plan?.occurrence ? (dataset.events[plan.occurrence.seriesId]?.repeat ?? null) : googlePlan?.repeat ?? null;
  // A Google series whose rule a Repeat can't say keeps it, and its Repeat can't change here.
  const customRepeat = googlePlan !== null && plan!.recurring && googlePlan.repeat === null;
  const calendars = google?.store.getState().calendars ?? [];
  const destinations = destinationOptions(calendars);
  const [destination, setDestination] = useState(() => draft?.destination !== undefined ? defaultDestination(draft.destination, destinations) : defaultDestination(dataset.settings.newEventsCalendar, destinations));
  const [calendarId, setCalendarId] = useState(googlePlan?.calendar.id ?? "");
  const toGoogle = googlePlan !== null || (!plan && destinationOf(destination) !== null);
  const movable = googlePlan !== null && !plan!.recurring ? writableCalendars(calendars).map((calendar) => ({ value: calendar.id, label: calendar.name, swatch: calendar.color })) : [];
  const [title, setTitle] = useState(seed.title);
  const [notes, setNotes] = useState(googlePlan ? plan!.notes : source?.notes ?? "");
  const [location, setLocation] = useState(googlePlan?.location ?? "");
  const [projectId, setProjectId] = useState(seed.projectId ?? "");
  const [taskId, setTaskId] = useState(seed.taskId ?? "");
  const [allDay, setAllDay] = useState(seed.allDay);
  const [day, setDay] = useState(startOfDay(seed.start));
  const [lastDay, setLastDay] = useState(seed.allDay ? startOfDay(seed.end - 1) : startOfDay(seed.start));
  const [start, setStart] = useState(seed.allDay ? "09:00" : toTimeInput(seed.start));
  const [end, setEnd] = useState(seed.allDay ? "10:00" : toTimeInput(seed.end));
  const [preset, setPreset] = useState<Preset>(presetOf(seriesRule, seed.start));
  const [custom, setCustom] = useState<RepeatRule>(seriesRule ?? { freq: "weekly", interval: 1, weekdays: [new Date(seed.start).getDay()], until: null, count: null });
  const [ends, setEnds] = useState<"never" | "on" | "after">(seriesRule?.until ? "on" : seriesRule?.count ? "after" : "never");
  const [error, setError] = useState<string | null>(null);

  const projects = projectOptions(dataset, seed.projectId);
  const tasks = projectId ? taskOptions(dataset, projectId, seed.taskId) : [];
  const weekday = WEEKDAY_NAMES[new Date(day).getDay()]!;
  const dayOfMonth = new Date(day).getDate();
  const customRule: RepeatRule = { ...custom, until: ends === "on" ? custom.until ?? dayKey(startOfDay(day, 30)) : null, count: ends === "after" ? custom.count ?? 10 : null };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    let from: number, to: number;
    if (allDay) {
      if (lastDay < day) return setError("The last day comes before the first.");
      from = day;
      to = startOfDay(lastDay, 1);
    } else {
      const parsedStart = fromTimeInput(day, start);
      let parsedEnd = fromTimeInput(day, end);
      if (parsedStart === null || parsedEnd === null) return setError("Enter a start and an end time.");
      if (parsedEnd <= parsedStart) parsedEnd = fromTimeInput(startOfDay(day, 1), end)!;
      from = parsedStart;
      to = parsedEnd;
    }
    const repeat = ruleOf(preset, from, customRule);
    if (repeat?.until && dayStartOf(repeat.until) < startOfDay(from)) return setError("The repeat ends before the event starts.");
    const fields = {
      title: title.trim(), notes: notes.trim(), projectId: projectId || null, taskId: taskId || null, allDay,
      start: from, end: to, startDay: allDay ? dayKey(from) : null, endDay: allDay ? dayKey(to - 1) : null
    };
    if (!plan) {
      if (toGoogle) {
        const problem = await commands.createDraft({ kind: "event", start: from, end: to, allDay }, { ...fields, location: location.trim(), destination: destinationOf(destination), repeat });
        if (problem) return setError(problem);
        return onClose();
      }
      await act(createEvent({ ...fields, repeat }, factory).operations, `Planned ${fields.title || "an event"}${allDay ? "" : `, ${formatClock(from)} to ${formatClock(to)}`}`);
      return onClose();
    }
    const patch: PlanPatch = {};
    if (fields.title !== plan.title) patch.title = fields.title;
    if (fields.notes !== (googlePlan ? plan.notes : source?.notes ?? "")) patch.notes = fields.notes;
    if (googlePlan && location.trim() !== googlePlan.location) patch.location = location.trim();
    if (googlePlan && calendarId && calendarId !== googlePlan.calendar.id) patch.calendarId = calendarId;
    if (fields.projectId !== plan.projectId) patch.projectId = fields.projectId;
    if (fields.taskId !== plan.taskId) patch.taskId = fields.taskId;
    if (allDay !== plan.allDay || from !== plan.start || to !== plan.end) Object.assign(patch, allDay ? { allDay, startDay: fields.startDay, endDay: fields.endDay } : { allDay, start: from, end: to });
    const repeatChanged = !customRepeat && !sameRule(repeat, seriesRule);
    if (repeatChanged) patch.repeat = repeat;
    if (!Object.keys(patch).length) return onClose();
    if (repeatChanged && !plan.recurring && plan.kind === "event") {
      // A single Event that starts repeating becomes the first of its series.
      await act([factory.op("event", plan.occurrence!.event.id, { ...patch })], `${fields.title || "Event"} now repeats`);
      return onClose();
    }
    const done = await commands.changePlan(plan, patch, `Updated ${fields.title || "the event"}`);
    if (done) onClose();
  };

  const remove = async () => {
    if (!plan) return onClose();
    onClose();
    await commands.removeItems([plan]);
  };

  const reveal = { initial: { opacity: 0, height: 0 }, animate: { opacity: 1, height: "auto" }, exit: { opacity: 0, height: 0 }, transition: { duration: 0.26, ease: [0.16, 1, 0.3, 1] as const }, style: { overflow: "hidden" as const } };

  return <Modal title={plan ? "Edit event" : "New event"} icon="calendar" description={googlePlan ? `In ${googlePlan.calendar.name}, Google Calendar. It never counts as tracked time.` : "Plans show on the Calendar and never count as tracked time."} onClose={onClose}>
    <form className="ot-form" onSubmit={(event) => void save(event)} noValidate>
      <Field label="Title">
        <TextInput value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} placeholder="e.g. Client review" autoFocus/>
      </Field>
      <div className="ot-form__pair">
        <Field label="Project" optional>
          <Select value={projectId} searchable={projects.length > 7} onChange={(next) => { setProjectId(next); setTaskId(""); }} options={projects}/>
        </Field>
        {tasks.length > 0 ? <Field label="Task" optional><Select value={taskId} onChange={setTaskId} options={tasks}/></Field> : <span/>}
      </div>
      <div className="ot-form__card">
        <Switch label="All day" description="Deadlines, trips, days off. Shown above the hours." checked={allDay} onChange={setAllDay}/>
      </div>
      {allDay ? <div className="ot-form__pair">
        <Field label="First day"><DateField value={day} onChange={(next) => { setDay(next); if (lastDay < next) setLastDay(next); }}/></Field>
        <Field label="Last day"><DateField value={lastDay} onChange={setLastDay}/></Field>
      </div> : <>
        <Field label="Day"><DateField value={day} onChange={setDay}/></Field>
        <div className="ot-times">
          <Field label="Start"><TimeField value={start} onChange={setStart}/></Field>
          <span className="ot-times__arrow" aria-hidden="true">→</span>
          <Field label="End"><TimeField value={end} onChange={setEnd}/></Field>
        </div>
      </>}
      {!plan && destinations.length > 1 && <Field label="Calendar">
        <Select value={destination} onChange={setDestination} options={destinations}/>
      </Field>}
      {movable.length > 1 && <Field label="Calendar">
        <Select value={calendarId} onChange={setCalendarId} options={movable}/>
      </Field>}
      {customRepeat ? <Field label="Repeat">
        <p className="ot-note"><Icon name="repeat"/><span>Custom, set in Google. Change how it repeats in Google Calendar; everything else can change here.</span></p>
      </Field> : <Field label="Repeat">
        <Select value={preset} onChange={setPreset} options={[
          { value: "none", label: "Does not repeat" },
          { value: "daily", label: "Every day", icon: "repeat" },
          { value: "weekdays", label: "Every weekday", hint: "Monday to Friday", icon: "repeat" },
          { value: "weekly", label: `Every week on ${weekday}`, icon: "repeat" },
          { value: "monthly", label: `Every month on day ${dayOfMonth}`, icon: "repeat" },
          { value: "custom", label: "Custom…", icon: "settings", divider: true }
        ]}/>
      </Field>}
      <AnimatePresence initial={false}>
        {preset === "custom" && <motion.div key="custom" {...reveal}>
          <div className="ot-repeat">
            <div className="ot-repeat__row">
              <span>Every</span>
              <TextInput size="sm" className="ot-repeat__number" inputMode="numeric" aria-label="Interval" value={String(custom.interval)} onChange={(event) => setCustom({ ...custom, interval: Math.min(99, Math.max(1, Number(event.target.value.replace(/\D/g, "")) || 1)) })}/>
              <Segmented size="sm" aria-label="Unit" value={custom.freq} onChange={(freq) => setCustom({ ...custom, freq })} options={[
                { value: "daily", label: custom.interval === 1 ? "day" : "days" },
                { value: "weekly", label: custom.interval === 1 ? "week" : "weeks" },
                { value: "monthly", label: custom.interval === 1 ? "month" : "months" }
              ]}/>
            </div>
            {custom.freq === "weekly" && <div className="ot-repeat__days" role="group" aria-label="On these days">
              {MONDAY_FIRST.map((value) => {
                const on = (custom.weekdays.length ? custom.weekdays : [new Date(day).getDay()]).includes(value);
                return <button key={value} type="button" aria-pressed={on} aria-label={WEEKDAY_NAMES[value]} className={`ot-repeat__day ${on ? "is-on" : ""}`} onClick={() => {
                  const current = custom.weekdays.length ? custom.weekdays : [new Date(day).getDay()];
                  const next = on ? current.filter((item) => item !== value) : [...current, value];
                  if (next.length) setCustom({ ...custom, weekdays: next });
                }}>{WEEKDAY_NAMES[value]!.slice(0, 2)}</button>;
              })}
            </div>}
            <div className="ot-repeat__row">
              <span>Ends</span>
              <Segmented size="sm" aria-label="Ends" value={ends} onChange={setEnds} options={[{ value: "never", label: "Never" }, { value: "on", label: "On a day" }, { value: "after", label: "After" }]}/>
              {ends === "on" && <DateField aria-label="Last repeat" value={dayStartOf(customRule.until!)} onChange={(next) => setCustom({ ...custom, until: dayKey(next) })}/>}
              {ends === "after" && <TextInput size="sm" className="ot-repeat__number" inputMode="numeric" aria-label="Occurrences" value={String(customRule.count)} trailing="times" onChange={(event) => setCustom({ ...custom, count: Math.min(999, Math.max(1, Number(event.target.value.replace(/\D/g, "")) || 1)) })}/>}
            </div>
          </div>
        </motion.div>}
      </AnimatePresence>
      {toGoogle && <Field label="Location" optional>
        <TextInput value={location} onChange={(event) => setLocation(event.target.value)} maxLength={2000} placeholder="Address or meeting link"/>
      </Field>}
      <Field label="Notes" optional>
        <TextArea value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={toGoogle ? 8000 : 4000} rows={3} placeholder="Agenda, links, what to prepare"/>
      </Field>
      {plan && plan.logged.length > 0 && <p className="ot-note"><Icon name="check-circle"/><span>Time was logged from this event. Changing it keeps that time as it is.</span></p>}
      <FieldError>{error}</FieldError>
      <div className="ot-form__actions">
        {plan && <Button type="button" tone="ghost" icon="trash" className="ot-button--quiet-danger" onClick={() => void remove()}>Delete</Button>}
        <span className="ot-form__spacer"/>
        <Button type="button" onClick={onClose}>Cancel</Button>
        <Button type="submit" tone="primary">{plan ? "Save" : "Create event"}</Button>
      </div>
    </form>
  </Modal>;
}
